import React, {useState} from "react";
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from "@tanstack/react-table";
import {Input} from "./ui/Input";
import {Button} from "./ui/Button";
import {PlusCircle} from "lucide-react";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "./ui/Table";
import {AlertDialog, AlertDialogTrigger} from "./ui/AlertDialog";

const DataTable = ({ columns, data, type }) => {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([])
    const [open, setOpen] = React.useState(false);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    })

    return (
        <div>
            {(() => {
                switch (type) {
                    case "businessRegister":
                        return (
                            <div className="flex flex-col md:flex-row items-center py-4 justify-between">
                                <Input
                                    placeholder="Filter business..."
                                    value={(table.getColumn("title")?.getFilterValue()) ?? ""}
                                    onChange={(event) =>
                                        table.getColumn("title")?.setFilterValue(event.target.value)
                                    }
                                    className="max-w-sm"
                                />
                                <AlertDialog onOpenChange={setOpen} open={open}>
                                    <AlertDialogTrigger asChild>
                                        <Button>
                                            <PlusCircle className="h-4 w-4 mr-2"/>
                                            Add New Business
                                        </Button>
                                    </AlertDialogTrigger>
                                    <div>
                                        <p>Alert dialog</p>
                                    </div>
                                </AlertDialog>
                            </div>
                        )
                    case "customers":
                        return (
                            <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 py-4 justify-between">
                                <Input
                                    placeholder="Filter customers..."
                                    value={(table.getColumn("title")?.getFilterValue()) ?? ""}
                                    onChange={(event) =>
                                        table.getColumn("title")?.setFilterValue(event.target.value)
                                    }
                                    className="max-w-sm"
                                />
                                <AlertDialog onOpenChange={setOpen} open={open}>
                                    <AlertDialogTrigger asChild>
                                        <Button className="w-full md:w-auto">
                                            <PlusCircle className="h-4 w-4 mr-2"/>
                                            Add New Customer
                                        </Button>
                                    </AlertDialogTrigger>
                                    <div>
                                        <p>Alert dialog</p>
                                    </div>
                                </AlertDialog>
                            </div>
                        )
                    case "rewards":
                        return (
                            <div className="flex flex-col md:flex-row items-center py-4 space-y-4 md:space-y-0 justify-between">
                                <Input
                                    placeholder="Filter rewards..."
                                    value={(table.getColumn("title")?.getFilterValue()) ?? ""}
                                    onChange={(event) =>
                                        table.getColumn("title")?.setFilterValue(event.target.value)
                                    }
                                    className="max-w-sm"
                                />
                                <AlertDialog onOpenChange={setOpen} open={open}>
                                    <AlertDialogTrigger asChild>
                                        <Button className="w-full md:w-auto">
                                            <PlusCircle className="h-4 w-4 mr-2"/>
                                            Add New Reward
                                        </Button>
                                    </AlertDialogTrigger>
                                    <div>
                                        <p>Alert dialog</p>
                                    </div>
                                </AlertDialog>
                            </div>
                        )
                    default:
                        return null
                }
            })()}

            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                >
                    Previous
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                >
                    Next
                </Button>
            </div>
        </div>
    )

}

export default DataTable